import { useLayoutEffect, useRef, useState, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { useLanguageCode } from '../../../../store/app/appStore';

// Max wait for an element's web font; matches the browsers' ~3s font block period.
const FONT_WAIT_MS = 3000;

// The element's own font if it isn't loaded yet (unparsable font strings count as loaded).
const getUnloadedFont = (el: HTMLElement): string | null => {
  const s = getComputedStyle(el);
  const font = `${s.fontStyle} ${s.fontWeight} ${s.fontSize} ${s.fontFamily}`;
  try {
    return document.fonts.check(font, el.textContent ?? '') ? null : font;
  } catch {
    return null;
  }
};

interface UseCampaignCardScaleOptions {
  maxWidth?: number;
  recalculateKey?: string | number | boolean;
  disabled?: boolean;
  /** Custom function to compute container width. When provided, this is used
   *  instead of the default parent offsetWidth walk. Useful for components
   *  like JoinInButton where the constraint is relative to a distant ancestor. */
  getContainerWidth?: () => number;
  /** Keep the element hidden while its own web font is still loading (max FONT_WAIT_MS) and
   *  reveal it with the scale measured in the real font. Text-only elements. */
  hideUntilFontLoaded?: boolean;
}

/**
 * Text scaling hook for campaign cards.
 *
 * Dynamically scales text to fit within its container. Attach `titleRef` to
 * the text element; the hook returns `scale` (1 = no scaling) and
 * `scaledWidth` (explicit px width after scaling, or undefined if unscaled).
 *
 * How it works, step by step:
 *
 * 1. **Natural width measurement** (`measureNaturalWidth`): Temporarily sets
 *    the element to `display: inline-block; white-space: nowrap; width: auto;
 *    transform: none`, reads `offsetWidth`, then restores styles. This gives
 *    the unconstrained text width.
 *
 * 2. **Container width measurement** (`measureContainerWidth`): Walks up the
 *    DOM from the element to find the first parent with non-zero `offsetWidth`.
 *    Falls back to `maxWidth`. Can be overridden with `getContainerWidth`.
 *
 * 3. **Scale computation** (`applyScale`): If naturalWidth > containerWidth,
 *    computes `scale = containerWidth / naturalWidth`, floored to 1 decimal
 *    place. Otherwise scale is 1.
 *
 * 4. **Caching**: Natural width is cached per (text, languageCode) pair. On
 *    fold/unfold or resize, only the container width is re-measured and scale
 *    is recomputed from cache — no DOM thrashing.
 *
 * 5. **Triggers for recalculation**:
 *    - Mount / deps change: rAF loop until measurement succeeds
 *    - Window resize: invalidates cache, re-measures everything
 *    - Custom event `force-text-scale`: for post-animation recalc
 *
 * Key design choice: Uses `offsetWidth` everywhere, which reports CSS pixels
 * unaffected by ancestor `transform: scale(...)`. This means it works
 * identically for active and non-active carousel cards without needing to
 * walk the transform chain.
 */
export const useCampaignCardScale = (
  text: string,
  options: UseCampaignCardScaleOptions = {}
) => {
  const { maxWidth, recalculateKey, disabled, getContainerWidth: customGetContainerWidth, hideUntilFontLoaded } = options;
  const titleRef = useRef<HTMLSpanElement | HTMLDivElement>(null);
  const [scale, setScale] = useState<number>(1);
  const [scaledWidth, setScaledWidth] = useState<number | undefined>(undefined);
  const prevScaleRef = useRef<number>(1);
  const languageCode = useLanguageCode();

  // Cache for natural width (only changes when text/language/recalculateKey changes)
  const naturalWidthRef = useRef<number>(0);
  const cachedForText = useRef<string>('');
  const cachedForLang = useRef<string>('');
  const cachedForRecalcKey = useRef<string | number | boolean | undefined>(undefined);
  const prevMaxWidthRef = useRef<number | undefined>(maxWidth);
  const fontsPendingRef = useRef(false);
  const settledFontRef = useRef<string | null>(null);
  const latestEnsureRef = useRef<() => boolean>(() => false);

  // Measure natural width, and optionally container width in the same reset pass
  const measure = useCallback((includeContainer: boolean): { naturalWidth: number; containerWidth: number } => {
    const el = titleRef.current;
    if (!el) return { naturalWidth: 0, containerWidth: 0 };

    const savedTransform = el.style.transform;
    const savedWidth = el.style.width;

    el.style.transform = 'none';
    el.style.width = 'auto';

    const naturalWidth = el.offsetWidth;
    let containerWidth = 0;
    if (includeContainer) {
      const parent = el.parentElement;
      if (parent) containerWidth = parent.clientWidth;
    }

    el.style.transform = savedTransform;
    el.style.width = savedWidth;

    return { naturalWidth, containerWidth };
  }, []);

  const measureContainerWidth = useCallback((): number => {
    if (maxWidth) return maxWidth;
    const el = titleRef.current;
    if (!el) return 0;
    const parent = el.parentElement;
    if (!parent) return 0;
    return parent.clientWidth;
  }, [maxWidth]);

  // Apply scale from natural width and container width (pure math)
  const prevScaledWidthRef = useRef<number | undefined>(undefined);
  const applyScale = useCallback((naturalWidth: number, containerWidth: number) => {
    if (naturalWidth <= containerWidth) {
      if (prevScaleRef.current !== 1) {
        setScale(1);
        setScaledWidth(undefined);
        prevScaleRef.current = 1;
        prevScaledWidthRef.current = undefined;
      }
      return;
    }

    const newScale = containerWidth / naturalWidth;
    const roundedScale = Math.floor(newScale * 10) / 10;
    const newScaledWidth = Math.floor(naturalWidth * roundedScale);

    if (prevScaleRef.current !== roundedScale || prevScaledWidthRef.current !== newScaledWidth) {
      setScale(roundedScale);
      setScaledWidth(newScaledWidth);
      prevScaleRef.current = roundedScale;
      prevScaledWidthRef.current = newScaledWidth;
    }
  }, []);

  // Core: measure natural width if needed, get container width, compute scale.
  const ensureMeasuredAndScale = useCallback(() => {
    // Never leave the element hidden by a font wait once scaling is disabled.
    if (disabled && fontsPendingRef.current && titleRef.current) titleRef.current.style.visibility = '';
    if (disabled || !titleRef.current) return false;

    const needsMeasurement =
      naturalWidthRef.current <= 0 ||
      cachedForText.current !== text ||
      cachedForLang.current !== languageCode ||
      cachedForRecalcKey.current !== recalculateKey;

    let containerWidth: number;

    if (needsMeasurement) {
      const needsContainerReset = !maxWidth && !customGetContainerWidth && prevScaleRef.current < 1;
      const measured = measure(needsContainerReset);
      if (measured.naturalWidth <= 0) return false;

      // The measure's layout may have just started loading the element's web font: that width is
      // the fallback font's. Hide, commit nothing, and once the font settles (loaded, failed or
      // FONT_WAIT_MS passed) re-measure what's rendered and reveal in the same paint.
      const el = titleRef.current;
      const pendingFont = hideUntilFontLoaded ? getUnloadedFont(el) : null;
      if (pendingFont && pendingFont !== settledFontRef.current) {
        if (!fontsPendingRef.current) {
          fontsPendingRef.current = true;
          el.style.visibility = 'hidden';
          let timer = 0;
          const settle = () => {
            window.clearTimeout(timer);
            fontsPendingRef.current = false;
            settledFontRef.current = pendingFont;
            naturalWidthRef.current = 0;
            flushSync(() => latestEnsureRef.current());
            el.style.visibility = '';
          };
          timer = window.setTimeout(settle, FONT_WAIT_MS);
          void document.fonts.load(pendingFont, el.textContent ?? '').then(settle, settle);
        }
        return false;
      }

      naturalWidthRef.current = measured.naturalWidth;
      cachedForText.current = text;
      cachedForLang.current = languageCode;
      cachedForRecalcKey.current = recalculateKey;
      containerWidth = customGetContainerWidth ? customGetContainerWidth() : (needsContainerReset ? measured.containerWidth : measureContainerWidth());
    } else {
      containerWidth = customGetContainerWidth ? customGetContainerWidth() : measureContainerWidth();
    }

    if (containerWidth <= 0) return false;

    applyScale(naturalWidthRef.current, containerWidth);
    return true;
  }, [text, languageCode, maxWidth, recalculateKey, disabled, hideUntilFontLoaded, measure, measureContainerWidth, customGetContainerWidth, applyScale]);

  // useLayoutEffect runs synchronously before browser paint,
  // preventing the flash of unscaled text (scale=1 → correct scale)
  useLayoutEffect(() => {
    latestEnsureRef.current = ensureMeasuredAndScale;
    // Invalidate cache if maxWidth changed (e.g., iPad rotation changing isMobile state)
    if (prevMaxWidthRef.current !== maxWidth) {
      naturalWidthRef.current = 0;
      prevMaxWidthRef.current = maxWidth;
    }
    ensureMeasuredAndScale();
  }, [ensureMeasuredAndScale, maxWidth]);

  // Window resize: invalidate cache and re-measure everything
  useLayoutEffect(() => {
    if (disabled) return;

    const handleResize = () => {
      naturalWidthRef.current = 0;
      ensureMeasuredAndScale();
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [ensureMeasuredAndScale, disabled]);

  // Post-animation recalculation
  useLayoutEffect(() => {
    if (disabled) return;

    const handler = () => {
      // The text itself changed, so the cached natural width is stale — same as on resize.
      naturalWidthRef.current = 0;
      ensureMeasuredAndScale();
    };

    window.addEventListener('force-text-scale', handler);
    return () => window.removeEventListener('force-text-scale', handler);
  }, [ensureMeasuredAndScale, disabled]);

  return { titleRef, scale, scaledWidth };
};
